import React, { useState, useEffect } from "react";
import QACard from "./QACard";
import "./App.css";

const API_BASE = process.env.REACT_APP_API_BASE || "https://rogen-backend-69lw.onrender.com";
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;

function generateId() {
  return "u_" + Math.random().toString(36).substring(2, 10) + Date.now().toString(36);
}

const SUGGESTIONS_GENERAL = [
  { q: "Ce este ROGEN?", icon: "🧬" },
  { q: "De ce avem nevoie de un genom românesc de referință?", icon: "🇷🇴" },
  { q: "Cine coordonează proiectul?", icon: "🏛️" },
];

const SUGGESTIONS_PARTICIPATION = [
  { q: "Cum mă pot înscrie în studiu?", icon: "✍️" },
  { q: "Ce se întâmplă cu proba mea biologică?", icon: "🧪" },
  { q: "Cum îmi sunt protejate datele genetice?", icon: "🔒" },
];

function DNAPattern() {
  return (
    <svg className="dna-bg" viewBox="0 0 1200 800" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <defs>
        <linearGradient id="dnaGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#1e3a8a" stopOpacity="0.08" />
          <stop offset="100%" stopColor="#f59e0b" stopOpacity="0.04" />
        </linearGradient>
      </defs>
      <g stroke="url(#dnaGrad)" strokeWidth="1.5" fill="none">
        {Array.from({ length: 12 }).map((_, i) => {
          const x = 100 + i * 90;
          return <path key={i} d={`M${x} 0 Q${x + 40} 400 ${x} 800`} />;
        })}
        {Array.from({ length: 12 }).map((_, i) => {
          const x = 140 + i * 90;
          return <path key={`b${i}`} d={`M${x} 0 Q${x - 40} 400 ${x} 800`} />;
        })}
      </g>
    </svg>
  );
}

export default function App() {
  const [conversation, setConversation] = useState([]);
  const [question, setQuestion] = useState("");
  const [hasAskedQuestion, setHasAskedQuestion] = useState(false);
  const [isAsking, setIsAsking] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [userId, setUserId] = useState("");
  const [conversationId, setConversationId] = useState(null);

  useEffect(() => {
    // Pre-warm backend on page load (avoid cold start when user asks first question)
    fetch(`${API_BASE}/healthz`, { method: "GET", keepalive: true }).catch(() => {});

    // Keep backend warm: ping /healthz every 4 minutes while the tab is open.
    // /healthz is instant (no Pinecone/Gemini call) so no cost. Render free
    // tier sleeps after 15 min idle — 4 min interval keeps it always awake.
    const keepAliveId = setInterval(() => {
      if (document.visibilityState === "visible") {
        fetch(`${API_BASE}/healthz`, { method: "GET", keepalive: true }).catch(() => {});
      }
    }, 4 * 60 * 1000);

    const last = Number(localStorage.getItem("rogenLastAsk") || 0);
    if (Date.now() - last > SESSION_TIMEOUT_MS) {
      localStorage.removeItem("rogenConversation");
      localStorage.removeItem("rogenConversationId");
      localStorage.removeItem("rogenHasAsked");
    }

    let uid = localStorage.getItem("rogenUserId");
    if (!uid) {
      uid = generateId();
      localStorage.setItem("rogenUserId", uid);
    }
    setUserId(uid);

    const savedCid = localStorage.getItem("rogenConversationId");
    if (savedCid) setConversationId(savedCid);

    const saved = localStorage.getItem("rogenConversation");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setConversation(parsed);
          setHasAskedQuestion(true);
        }
      } catch (e) {}
    }

    return () => clearInterval(keepAliveId);
  }, []);

  useEffect(() => {
    localStorage.setItem("rogenConversation", JSON.stringify(conversation));
    localStorage.setItem("rogenHasAsked", hasAskedQuestion ? "true" : "false");
  }, [conversation, hasAskedQuestion]);

  function handleNewConversation() {
    setConversation([]);
    setConversationId(null);
    setHasAskedQuestion(false);
    setQuestion("");
    setErrorMsg("");
    localStorage.removeItem("rogenConversation");
    localStorage.removeItem("rogenConversationId");
    localStorage.removeItem("rogenHasAsked");
    localStorage.removeItem("rogenLastAsk");
  }

  function updateLastItem(patch) {
    setConversation((prev) => {
      if (prev.length === 0) return prev;
      const copy = [...prev];
      copy[copy.length - 1] = { ...copy[copy.length - 1], ...patch };
      return copy;
    });
  }

  async function askQuestion(text) {
    setConversation((prev) => [...prev, { question: text, answerObj: null, isLoading: true }]);
    try {
      const res = await fetch(`${API_BASE}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: text,
          conversation_id: conversationId,
          user_id: userId,
        }),
      });
      if (!res.ok) throw new Error("Server status " + res.status);
      const data = await res.json();
      if (data.conversation_id && data.conversation_id !== conversationId) {
        setConversationId(data.conversation_id);
        localStorage.setItem("rogenConversationId", data.conversation_id);
      }
      updateLastItem({
        answerObj: {
          id: data.message_id,
          answer: data.answer || "",
          references: Array.isArray(data.references) ? data.references : [],
        },
        isLoading: false,
      });
    } catch (err) {
      updateLastItem({
        answerObj: { answer: "Îmi pare rău, a apărut o eroare: " + err.message, references: [] },
        isLoading: false,
      });
      setErrorMsg(err.message);
    }
  }

  async function handleAsk(e) {
    e.preventDefault();
    if (!question.trim() || isAsking || !userId) return;
    setIsAsking(true);
    setErrorMsg("");
    if (!hasAskedQuestion) setHasAskedQuestion(true);
    localStorage.setItem("rogenLastAsk", Date.now().toString());
    const q = question;
    setQuestion("");
    await askQuestion(q);
    setIsAsking(false);
  }

  function pickSuggestion(text) {
    setQuestion(text);
  }

  return (
    <>
      <DNAPattern />

      <header className="navbar">
        <button
          type="button"
          className="navbar-brand"
          onClick={handleNewConversation}
          aria-label="Pagina principală — conversație nouă"
        >
          <img src="/assets/rogen-icon.png" alt="ROGEN" className="nav-logo-img" />
          <div className="nav-brand-text">
            <span className="navbar-title">ROGEN AI</span>
            <span className="navbar-subtitle">Asistent virtual</span>
          </div>
        </button>
        <div className="navbar-actions">
          <a
            href="https://rogen.umfcd.ro"
            target="_blank"
            rel="noreferrer"
            className="navbar-link"
          >
            rogen.umfcd.ro ↗
          </a>
          <button className="navbar-btn" onClick={handleNewConversation}>
            Conversație nouă
          </button>
        </div>
      </header>

      {errorMsg && <div className="error-msg">Eroare: {errorMsg}</div>}

      {!hasAskedQuestion ? (
        <main className="intro-screen">
          <div className="intro-eyebrow">
            <span className="dot" />
            Programul Național Genomic al României · 2024–2029
          </div>

          <h1 className="intro-heading">
            Întrebări despre <span className="heading-accent">ROGEN</span>?
            <br />
            <span className="heading-serif">Am răspunsurile.</span>
          </h1>

          <p className="intro-subtext">
            Asistent AI al proiectului ROGEN — primul program național genomic al României.
            Întreabă despre studiu, participare, genomică sau confidențialitate.
          </p>

          <div className="stats-row">
            <div className="stat">
              <div className="stat-val">5.000+</div>
              <div className="stat-label">voluntari</div>
            </div>
            <div className="stat">
              <div className="stat-val">20</div>
              <div className="stat-label">parteneri</div>
            </div>
            <div className="stat">
              <div className="stat-val">422M</div>
              <div className="stat-label">RON buget</div>
            </div>
            <div className="stat">
              <div className="stat-val">5</div>
              <div className="stat-label">ani (2024–29)</div>
            </div>
          </div>

          <form className="ask-form" onSubmit={handleAsk}>
            <input
              type="text"
              placeholder="Ce vrei să știi despre ROGEN?"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              disabled={isAsking || !userId}
            />
            <button type="submit" disabled={isAsking || !userId} aria-label="Trimite">
              <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
              </svg>
            </button>
          </form>

          <div className="suggestions-block">
            <div className="suggestions-label">Despre proiect</div>
            <div className="intro-suggestions">
              {SUGGESTIONS_GENERAL.map((s) => (
                <button key={s.q} onClick={() => pickSuggestion(s.q)}>
                  <span className="sg-icon">{s.icon}</span> {s.q}
                </button>
              ))}
            </div>
          </div>

          <div className="suggestions-block">
            <div className="suggestions-label">Participare la studiu</div>
            <div className="intro-suggestions">
              {SUGGESTIONS_PARTICIPATION.map((s) => (
                <button key={s.q} onClick={() => pickSuggestion(s.q)}>
                  <span className="sg-icon">{s.icon}</span> {s.q}
                </button>
              ))}
            </div>
          </div>
        </main>
      ) : (
        <>
          <main className="conversation-scroll">
            <div className="conversation-container">
              {conversation.map((item, idx) => (
                <QACard
                  key={idx}
                  question={item.question}
                  answerObj={item.answerObj}
                  isLoading={item.isLoading}
                  isActive={idx === conversation.length - 1}
                  apiBase={API_BASE}
                />
              ))}
            </div>
          </main>
          <div className="pinned-bottom-area">
            <form className="ask-form pinned-ask" onSubmit={handleAsk}>
              <input
                type="text"
                placeholder="Întreabă orice despre ROGEN…"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                disabled={isAsking || !userId}
              />
              <button type="submit" disabled={isAsking || !userId} aria-label="Trimite">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                  <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                </svg>
              </button>
            </form>
          </div>
        </>
      )}

      <footer className="site-footer">
        <div className="footer-inner">
          <img src="/assets/rogen-logo.png" alt="Cofinanțat de Uniunea Europeană · Guvernul României · Program Sănătate" className="footer-banner" />
          <div className="footer-text">
            <strong>ROGEN</strong> — Dezvoltarea cercetării genomice în România · Cod MySMIS 324809 · Beneficiar: UMF „Carol Davila" București · 2024–2029
            <br />
            Acest asistent AI este un prototip independent. Informații oficiale:{" "}
            <a href="https://rogen.umfcd.ro" target="_blank" rel="noreferrer">rogen.umfcd.ro</a>
          </div>
        </div>
      </footer>
    </>
  );
}
